import { createContext, useContext, useEffect, useMemo, useState } from "react";

export const BASE_W = 1440;
export const BASE_H = 900;
const MIN_SCALE = 0.6;
const MAX_SCALE = 2;

// Tablet landscape (4:3 to 3:2): a narrower canvas with the same three columns, slightly tighter,
// so the whole game is drawn bigger on iPad-style screens instead of being letterboxed.
export const COMPACT_MIN_W = 1280;
const COMPACT_BELOW_ASPECT = 1.5;
const COMPACT_MIN_SCALE = 0.5;

const StageContext = createContext(null);
const LayoutContext = createContext({ compact: false, width: BASE_W, height: BASE_H });
// Portal target so dialogs, sheets and popovers scale with the stage.
export const useStageContainer = () => useContext(StageContext) || undefined;
// { compact, width, height } of the current canvas (stage px).
export const useStageLayout = () => useContext(LayoutContext);

function measure() {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const compact = W / H < COMPACT_BELOW_ASPECT;
  const bw = compact ? Math.min(BASE_W, Math.max(COMPACT_MIN_W, Math.round((BASE_H * W) / H))) : BASE_W;
  const bh = BASE_H;
  const lo = compact ? COMPACT_MIN_SCALE : MIN_SCALE;
  const s = Math.min(MAX_SCALE, Math.max(lo, Math.min(W / bw, H / bh)));
  return { s, bw, bh, compact, x: Math.round((W - bw * s) / 2), y: Math.round((H - bh * s) / 2) };
}

function useStageFit() {
  const [fit, setFit] = useState(measure);
  useEffect(() => {
    const on = () => setFit(measure());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    document.addEventListener("fullscreenchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
      document.removeEventListener("fullscreenchange", on);
    };
  }, []);
  return fit;
}

// Converts a viewport point (getBoundingClientRect) into stage coordinates.
export function toStage(x, y) {
  const el = document.querySelector('[data-testid="stage"]');
  if (!el) return { x, y };
  const r = el.getBoundingClientRect();
  const s = Number(el.dataset.scale) || 1;
  return { x: (x - r.left) / s, y: (y - r.top) / s };
}

// The game screen as one canvas, scaled uniformly and centred.
// Desktop / wide (aspect >= 1.5): 1440x900, min(W / 1440, H / 900), clamped 0.6x to 2x.
// Tablet landscape (aspect < 1.5): 1280..1440 x 900 (width follows the screen's aspect), compact columns.
// children: nodes, or a function ({ compact, width, height }) => nodes.
export default function Stage({ children }) {
  const fit = useStageFit();
  const [el, setEl] = useState(null);
  const s = fit.s;
  useEffect(() => {
    document.documentElement.style.setProperty("--stage-scale", String(s));
    return () => document.documentElement.style.removeProperty("--stage-scale");
  }, [s]);
  const layout = useMemo(() => ({ compact: fit.compact, width: fit.bw, height: fit.bh }), [fit.compact, fit.bw, fit.bh]);

  return (
    <div data-testid="stage-viewport" className="fixed inset-0 overflow-hidden" style={{ background: "var(--table)" }}>
      <div
        ref={setEl}
        data-testid="stage"
        data-scale={s}
        data-layout={fit.compact ? "compact" : "wide"}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: fit.bw,
          height: fit.bh,
          overflow: "hidden",
          transform: `translate(${fit.x}px, ${fit.y}px) scale(${s})`,
          transformOrigin: "top left",
          "--svh": `${fit.bh / 100}px`,
          "--svw": `${fit.bw / 100}px`,
        }}
      >
        <LayoutContext.Provider value={layout}>
          <StageContext.Provider value={el}>{typeof children === "function" ? children(layout) : children}</StageContext.Provider>
        </LayoutContext.Provider>
      </div>
    </div>
  );
}
